"use client";

import { useState } from "react";
import dynamic from "next/dynamic";
import { Download, Loader2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import type { StockReportFairValue } from "@/components/pdf/stock-report-document";
import type { KpiScoreEntry } from "@/lib/scoring/types";

const StockReportDownloadLink = dynamic(
  () => import("@/components/pdf/stock-report-download-link").then((m) => m.StockReportDownloadLink),
  {
    ssr: false,
    loading: () => (
      <Button type="button" variant="outline" size="sm" disabled className="gap-1.5">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        PDF wird vorbereitet…
      </Button>
    ),
  },
);

/**
 * Ein-Seiten-Report-Export (backlog.md -> C19) - explizit als Premium-Feature
 * konzipiert (kleines Sparkles-Badge signalisiert das), aber wie der gesamte übrige
 * Depot-Röntgenblick-Bereich aktuell NICHT hinter der Paywall gesperrt - die Paywall
 * ist projektweit bewusst deaktiviert, bis Erik reale Nutzerzahlen sieht (siehe
 * PaywallGate/PAYMENTS_ENABLED, backlog.md -> "Monetarisierung"). Reaktivierung ist ein
 * triviales Follow-up (gleiches Muster wie beim Depot-Check), kein Code-Umbau nötig.
 */
export function StockReportButton({
  name,
  symbol,
  currency,
  score,
  summary,
  dataQuality,
  kpis,
  fairValue,
}: {
  name: string;
  symbol: string;
  currency?: string;
  score: number;
  summary: string;
  dataQuality: number;
  kpis: KpiScoreEntry[];
  fairValue: StockReportFairValue;
}) {
  const [requested, setRequested] = useState(false);

  return (
    <span className="inline-flex items-center gap-1.5">
      {requested ? (
        <StockReportDownloadLink
          name={name}
          symbol={symbol}
          currency={currency}
          score={score}
          summary={summary}
          dataQuality={dataQuality}
          kpis={kpis}
          fairValue={fairValue}
        />
      ) : (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="gap-1.5"
          onClick={() => setRequested(true)}
        >
          <Download className="h-3.5 w-3.5" />
          Report als PDF
        </Button>
      )}
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex items-center text-gold-text" aria-label="Premium-Funktion">
            <Sparkles className="h-3.5 w-3.5" />
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-56">
          Als Premium-Funktion vorgesehen - aktuell wie der gesamte Depot-Röntgenblick-
          Bereich kostenlos nutzbar, solange die Paywall pausiert ist.
        </TooltipContent>
      </Tooltip>
    </span>
  );
}
